import type { DriveStep, DriverHook } from "driver.js";
import type { TourSidebarPanel } from "./types";

export interface MobileTourStepCopy {
  closingDescription: string;
  closingTitle: string;
  coachSuggestionsDescription: string;
  coachSuggestionsTitle: string;
  composerDescription: string;
  composerTitle: string;
  historyDescription: string;
  historyTitle: string;
  savedPromptsDescription: string;
  savedPromptsTitle: string;
  scopeDescription: string;
  scopeTitle: string;
  settingsDescription: string;
  settingsTitle: string;
  sidebarDescription: string;
  sidebarTitle: string;
  welcomeDescription: string;
  welcomeTitle: string;
}

interface MobileTourStepController {
  collapseSidebar: () => void;
  selectSidebarPanel: (panel: TourSidebarPanel) => void;
}

interface MobileTourStepOptions {
  controller: MobileTourStepController;
  copy: MobileTourStepCopy;
}

const createPanelTransition =
  (
    controller: MobileTourStepController,
    panel: TourSidebarPanel,
    direction: "next" | "previous",
  ): DriverHook =>
  (_element, _step, hookOptions) => {
    controller.selectSidebarPanel(panel);
    if (direction === "next") {
      hookOptions.driver.moveNext();
      return;
    }

    hookOptions.driver.movePrevious();
  };

const createComposerTransition =
  (
    controller: MobileTourStepController,
    direction: "next" | "previous",
  ): DriverHook =>
  (_element, _step, hookOptions) => {
    controller.collapseSidebar();
    if (direction === "next") {
      hookOptions.driver.moveNext();
      return;
    }

    hookOptions.driver.movePrevious();
  };

export const createCoachInterfaceIntroMobileSteps = ({
  controller,
  copy,
}: MobileTourStepOptions): DriveStep[] => [
  {
    data: { tourStep: "welcome" },
    popover: {
      title: copy.welcomeTitle,
      description: copy.welcomeDescription,
      showButtons: ["next", "close"],
    },
  },
  {
    data: { tourStep: "scope" },
    element: '[data-tour="scope-controls"]',
    waitForElement: 1_000,
    popover: {
      title: copy.scopeTitle,
      description: copy.scopeDescription,
      side: "bottom",
      align: "start",
    },
  },
  {
    data: { tourStep: "sidebar-toggle" },
    element: '[data-tour="mobile-sidebar-toggle"]',
    waitForElement: 1_000,
    popover: {
      title: copy.sidebarTitle,
      description: copy.sidebarDescription,
      side: "bottom",
      align: "start",
      onNextClick: createPanelTransition(controller, "history", "next"),
    },
  },
  {
    data: { tourStep: "history" },
    element: '[data-tour="history-panel"]',
    waitForElement: 1_000,
    popover: {
      title: copy.historyTitle,
      description: copy.historyDescription,
      side: "bottom",
      align: "center",
      onNextClick: createPanelTransition(controller, "saved-prompts", "next"),
      onPrevClick: createComposerTransition(controller, "previous"),
    },
  },
  {
    data: { tourStep: "saved-prompts" },
    element: '[data-tour="saved-prompts-panel"]',
    waitForElement: 1_000,
    popover: {
      title: copy.savedPromptsTitle,
      description: copy.savedPromptsDescription,
      side: "bottom",
      align: "center",
      onNextClick: createPanelTransition(
        controller,
        "coach-suggestions",
        "next",
      ),
      onPrevClick: createPanelTransition(controller, "history", "previous"),
    },
  },
  {
    data: { tourStep: "coach-suggestions" },
    element: '[data-tour="coach-suggestions-panel"]',
    waitForElement: 1_000,
    popover: {
      title: copy.coachSuggestionsTitle,
      description: copy.coachSuggestionsDescription,
      side: "bottom",
      align: "center",
      onNextClick: createPanelTransition(controller, "settings", "next"),
      onPrevClick: createPanelTransition(
        controller,
        "saved-prompts",
        "previous",
      ),
    },
  },
  {
    data: { tourStep: "settings" },
    element: '[data-tour="settings-panel"]',
    waitForElement: 1_000,
    popover: {
      title: copy.settingsTitle,
      description: copy.settingsDescription,
      side: "bottom",
      align: "center",
      onNextClick: createComposerTransition(controller, "next"),
      onPrevClick: createPanelTransition(
        controller,
        "coach-suggestions",
        "previous",
      ),
    },
  },
  {
    data: { tourStep: "composer" },
    element: '[data-tour="message-composer"]',
    waitForElement: 1_000,
    popover: {
      title: copy.composerTitle,
      description: copy.composerDescription,
      side: "top",
      align: "center",
      onNextClick: createPanelTransition(controller, "guided-tours", "next"),
      onPrevClick: createPanelTransition(controller, "settings", "previous"),
    },
  },
  {
    data: { tourStep: "guided-tours" },
    element: '[data-tour="guided-tours-panel"]',
    waitForElement: 1_000,
    popover: {
      title: copy.closingTitle,
      description: copy.closingDescription,
      side: "bottom",
      align: "center",
      onPrevClick: createComposerTransition(controller, "previous"),
    },
  },
];
